import React, { lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

/**
 * Mount point for the app-wide dialogs.
 *
 * All eighteen used to be statically imported and unconditionally rendered, so
 * every dashboard page mounted eighteen live Redux subscribers to display
 * nothing, and 257 KB of feature code -- the integration dialogs, the
 * onboarding flow, the deal and meeting creation forms, CRMDealPicker -- sat in
 * the initial bundle for every user.
 *
 * Each dialog already read its own open flag from `uiStates` with a
 * field-level selector, so the gates below simply hoist that same read one
 * level up. Nothing about the dialogs' own state or props changed.
 *
 * Building the element (`<ZoomIntegrationDialog />`) does not load its chunk --
 * only *rendering* a lazy component invokes its loader -- so a gate that
 * returns null never touches the network.
 */

// Integrations
const ReintegrateCalendarDialog = lazy(() =>
  import('@/features/integrations/ReintegrateCalendarDialog'),
);
const CrmOrToolDoesntExistDialog = lazy(() =>
  import('@/features/integrations/CrmOrToolDoesntExistDialog'),
);
const ForcedTurnedOffFeatureDialog = lazy(() =>
  import('@/features/integrations/ForcedTurnedOffFeatureDialog'),
);
const DisclosureDialog = lazy(() =>
  import('@/features/integrations/DisclosureDialog'),
);
const HubspotKeyDialog = lazy(() =>
  import('@/features/integrations/HubspotKeyDialog'),
);
const ZohoKeyDialog = lazy(() =>
  import('@/features/integrations/ZohoKeyDialog'),
);
const FathomKeyDialog = lazy(() =>
  import('@/features/integrations/FathomKeyDialog'),
);
const AircallIntegrationDialog = lazy(() =>
  import('@/features/integrations/AircallIntegrationDialog'),
);
const SlackIntegrationDialog = lazy(() =>
  import('@/features/integrations/SlackIntegrationDialog'),
);
const SlackOAuthConfigDialog = lazy(() =>
  import('@/features/integrations/SlackOAuthConfigDialog'),
);
const ZoomIntegrationDialog = lazy(() =>
  import('@/features/integrations/ZoomIntegrationDialog'),
);
const GmailIntegrationDialog = lazy(() =>
  import('@/features/integrations/GmailIntegrationDialog'),
);

// Onboarding
const OnboardingCompletedDialog = lazy(() =>
  import('@/features/onboarding/OnboardingCompletedDialog'),
);
const OnboardingDialogV2 = lazy(() =>
  import('@/features/onboarding/OnboardingDialogV2'),
);

// Deals & meetings
const NewStakeholderOrSellerDialog = lazy(() =>
  import('@/features/deals/NewStakeholderOrSellerDialog'),
);
const CreateNewDealDialog = lazy(() =>
  import('@/features/deals/CreateNewDealDialog'),
);
const CreateMeetingItemDialog = lazy(() =>
  import('@/features/meetings/CreateMeetingItemDialog'),
);
const MeetingObjectivesDialog = lazy(() =>
  import('@/features/meetings/MeetingObjectivesDialog'),
);

// Product tours
const WalkthroughsListDialog = lazy(() =>
  import('@/features/productTours/WalkthroughsListDialog'),
);

/**
 * Mounts its child only while `field` in `uiStates` is truthy.
 *
 * One component per gate rather than eighteen selectors in `GlobalDialogs`, so
 * a flag change re-renders only its own gate instead of the whole mount point.
 *
 * @param {Object} props
 * @param {string} props.field the `uiStates` key holding the dialog's open flag
 * @param {React.ReactNode} props.children the dialog to mount
 */
const WhenUiFlag = ({ field, children }) => {
  const isOpen = useSelector((state) => state.uiStates[field]);
  if (!isOpen) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};

WhenUiFlag.propTypes = {
  field: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * As {@link WhenUiFlag}, but also mounts while a product tour is running.
 *
 * `CreateNewDealDialog` and `CreateMeetingItemDialog` are the only two dialogs
 * that open *themselves*: each holds an effect that force-opens it when the
 * active tour step declares a `forceOpenKey` (see `toursRegistry.js:220` and
 * `:784`, consumed in `useDealCreationForm.js:252` and
 * `CreateMeetingItemDialog.js:209`). That effect runs precisely while the
 * dialog is closed, so gating them on their own flag alone would unmount the
 * only thing able to set it, and both tour walkthroughs would stall on a
 * dialog that never appears.
 *
 * Mounting them for the duration of a tour costs nothing in practice -- tours
 * are rare and short -- and keeps the force-open behaviour exactly as it was.
 *
 * @param {Object} props
 * @param {string} props.field the `uiStates` key holding the dialog's open flag
 * @param {React.ReactNode} props.children the dialog to mount
 */
const WhenUiFlagOrTour = ({ field, children }) => {
  const isOpen = useSelector((state) => state.uiStates[field]);
  const isTourRunning = useSelector((state) => state.productTour.isRunning);
  if (!isOpen && !isTourRunning) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};

WhenUiFlagOrTour.propTypes = {
  field: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

/**
 * As {@link WhenUiFlag}, but reading the flag from the `productTour` slice.
 *
 * The walkthroughs picker used to be local state inside `SidebarNav`, which
 * put it inside the sidebar's Drawer -- unmounted on mobile whenever the
 * drawer is closed -- and out of reach of anything else. It now opens from two
 * places: the sidebar's help button, and the tour runner, which reopens it
 * when a walkthrough is skipped so the user lands back on the list.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children the dialog to mount
 */
const WhenWalkthroughsListOpen = ({ children }) => {
  const isOpen = useSelector(
    (state) => state.productTour.isWalkthroughsListOpen,
  );
  if (!isOpen) return null;
  return <Suspense fallback={null}>{children}</Suspense>;
};

WhenWalkthroughsListOpen.propTypes = {
  children: PropTypes.node.isRequired,
};

const GlobalDialogs = ({ gmailReadOnly, integrations }) => {
  return (
    <>
      <WhenUiFlag field="showReintegrateCalendarDialog">
        <ReintegrateCalendarDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showOnboardingCompleted">
        <OnboardingCompletedDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showCrmOrToolDoesntExist">
        <CrmOrToolDoesntExistDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showForcedTurnedOffFeature">
        <ForcedTurnedOffFeatureDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showDisclosure">
        <DisclosureDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showHubspotKeyDialog">
        <HubspotKeyDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showZohoKeyDialog">
        <ZohoKeyDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showFathomKeyDialog">
        <FathomKeyDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showAircallIntegrationDialog">
        <AircallIntegrationDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showSlackIntegrationDialog">
        <SlackIntegrationDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showSlackOAuthConfigDialog">
        <SlackOAuthConfigDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showZoomIntegrationDialog">
        <ZoomIntegrationDialog />
      </WhenUiFlag>
      <WhenUiFlag field="showGmailIntegrationDialog">
        <GmailIntegrationDialog gmailReadOnly={gmailReadOnly} />
      </WhenUiFlag>
      <WhenUiFlag field="showOnboardingDialogV2">
        <OnboardingDialogV2 integrations={integrations} />
      </WhenUiFlag>
      <WhenUiFlag field="newStakeholderOrSellerDetected">
        <NewStakeholderOrSellerDialog />
      </WhenUiFlag>
      <WhenUiFlagOrTour field="showCreateNewDealDialog">
        <CreateNewDealDialog />
      </WhenUiFlagOrTour>
      <WhenUiFlagOrTour field="showCreateMeetingItemDialog">
        <CreateMeetingItemDialog
          googleIntegration={integrations?.googleIntegration}
        />
      </WhenUiFlagOrTour>
      <WhenUiFlag field="showMeetingObjectives">
        <MeetingObjectivesDialog />
      </WhenUiFlag>
      <WhenWalkthroughsListOpen>
        <WalkthroughsListDialog />
      </WhenWalkthroughsListOpen>
    </>
  );
};

GlobalDialogs.propTypes = {
  gmailReadOnly: PropTypes.bool,
  integrations: PropTypes.object.isRequired,
};

export default GlobalDialogs;
